import { FC, useRef, useMemo, useState, useEffect } from 'react'
import {
  Paper,
  Box,
  CircularProgress,
  IconButton,
  styled,
  TableContainer,
  Table,
} from '@mui/material'
import EditIcon from '@/assets/icons/actions/edit.svg'
import {
  TableWrapper,
  TableContainerStyled,
  VirtualTableContainer,
  VirtualRow,
  VirtualCell,
} from '@/components/table/StyledTableComponents'
import { TableHeader } from '@/components/table/TableHeader'
import { NoResultsAlert } from '@/components/notification/NoResultsAlert'
import type { UserSortableTableProps } from '@/utilities/types/table-props'
import type { TableColumn, UserData } from '@/utilities/types/table'
import dayjs from 'dayjs'
import { useUserVirtualizedTable } from '@/hooks'
import { UserTableFooter } from '../../UserTableFooter'

const LoadingContainer = styled(Box)({
  p: 4,
  textAlign: 'center',
  height: 'calc(90vh - 60px)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
})

const columns: TableColumn[] = [
  { id: 'id', label: 'ID', width: '10%', sortable: true },
  { id: 'fullName', label: 'Full Name', width: '15%', sortable: true },
  { id: 'roleName', label: 'Roles', width: '15%', sortable: true },
  { id: 'dateCreated', label: 'Created Date', width: '20%', sortable: true },
  { id: 'dateModified', label: 'Modified Date', width: '20%', sortable: true },
  { id: 'action', label: 'Actions', width: '20%', align: 'center' },
]

interface TableProps extends UserSortableTableProps<UserData> {}

export const UserManagementTable: FC<TableProps> = ({
  data = [],
  filterState,
  isLoading,
  error,
  onSort,
  handleUpdate,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  totalElem,
  handleSort,
}) => {
  console.log('table data', data)

  const parentRef = useRef<HTMLDivElement>(null)
  const [showNoResults, setShowNoResults] = useState(false)

  const {
    virtualRows,
    totalSize,
    paginatedItems,
    handlePageChange,
    handleRowsPerPageChange,
    handleVirtualizedScroll,
  } = useUserVirtualizedTable({
    data,
    filterState,
    containerRef: parentRef,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    totalElem,
  })



  const renderVirtualRows = useMemo(() => {
    return virtualRows.map((virtualRow) => {
      const row = paginatedItems[virtualRow.index]
      if (!row) return null

      return (
        <VirtualRow
          key={`${row.id}-${page}`}
          sx={{
            transform: `translateY(${virtualRow.start}px)`,
            backgroundColor: virtualRow.index % 2 === 0 ? '#ffffff' : '#f5f5f5',
            display: 'flex',
            height: '52px',
            padding: '16px',
            alignItems: 'center',
          }}
        >
          {columns.map((column) => (
            <VirtualCell
              key={`${column.id}-${row.id}`}
              sx={{
                width: column.width,
                textTransform: 'uppercase',
              }}
            >
              {column.id === 'action' ? (
                <IconButton
                  sx={{
                    padding: '4px',
                    '&:hover': { bgcolor: 'rgba(73, 90, 255, 0.05)' },
                  }}
                  onClick={() => handleUpdate(row)}
                >
                  <img src={EditIcon} alt="Edit" width={16} height={16} />
                </IconButton>
              ) : (
                (() => {
                  if (column.id === 'fullName') return row.fullName                
                  if (column.id === 'roleName') return row.roleName
                  if (column.id === 'dateCreated' || column.id === 'dateModified') {
                      const dateValue = row[column.id as keyof UserData]
                      if (typeof dateValue === 'string' || typeof dateValue === 'number') {
                        return dayjs(dateValue).format('MMM DD, YYYY')
                      }
                      return 'Invalid Date'
                    }
                    return row[column.id as keyof UserData] as React.ReactNode
                  })()
              )}
            </VirtualCell>
          ))}
        </VirtualRow>
      )
    })
  }, [virtualRows, paginatedItems, page])

  if (isLoading) {
    return (
      <LoadingContainer>
        <CircularProgress />
      </LoadingContainer>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>
        Error: {error instanceof Error ? error.message : 'Unknown error'}
      </Box>
    )
  }

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{
          overflowY: 'auto',
          zIndex: 1,
          overflowX: 'scroll',
          marginTop: '5px',
        }}
      >
        <Table aria-label="user management table">
          <TableHeader
            columns={columns}
            filterState={filterState}
            handleSort={handleSort}
          />
          <VirtualTableContainer
            ref={parentRef}
            onScroll={handleVirtualizedScroll}
          >
            <div
              style={{
                height: `${totalSize}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {renderVirtualRows}
            </div>
          </VirtualTableContainer>
        </Table>
      </TableContainer>
      <UserTableFooter
        rowsPerPage={rowsPerPage}
        totalCount={totalElem}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </>
  )
}
